import { ItemAvailabilityStatus } from 'enums/menu';
import { IDayPartBoundary, IValidDayPart } from 'state/menu/main-menu/types';
import { ItemAvailability } from 'state/menu/menu-options/types';

import { ICartEntry } from '../types';

export type GetOneMenuItemAvailability = (itemId: string) => ItemAvailability;

export type InvalidLineDiagnostic =
  | {
      kind: 'unavailable';
      lineMode: 'product' | 'offer' | 'reward';
      lineRootName: string;
      lineRootId: string;
      failingName: string;
      failingId: string;
      status: ItemAvailabilityStatus;
      /** Ancestors of the failing node (root → parent), not including the failing node */
      ancestorPath: string[];
    }
  | {
      kind: 'unexpected_available';
      lineMode: 'product' | 'offer' | 'reward';
      lineRootName: string;
      lineRootId: string;
    }
  | {
      kind: 'missing_benefit';
      lineMode: 'offer' | 'reward';
      lineRootName: string;
      lineRootId: string;
    };

/**
 * Same traversal order as useCartValidation `isCartEntryTreeAvailable`: check node, then each child in order.
 */
export function findFirstNonAvailableNode(
  entry: ICartEntry,
  getOneMenuItemAvailability: GetOneMenuItemAvailability,
  ancestorLabels: string[]
): { failing: ICartEntry; status: ItemAvailabilityStatus; ancestorPath: string[] } | null {
  const { availabilityStatus } = getOneMenuItemAvailability(entry.id);
  if (availabilityStatus !== ItemAvailabilityStatus.AVAILABLE) {
    return { failing: entry, status: availabilityStatus, ancestorPath: ancestorLabels };
  }
  for (const child of entry.children) {
    const nextAncestors = [...ancestorLabels, `${entry.name} (${entry.id})`];
    const found = findFirstNonAvailableNode(child, getOneMenuItemAvailability, nextAncestors);
    if (found) {
      return found;
    }
  }
  return null;
}

function getLineRootId(entry: ICartEntry): string {
  return entry.cartId ?? entry.id;
}

export function diagnoseInvalidModalLine(
  entry: ICartEntry,
  getOneMenuItemAvailability: GetOneMenuItemAvailability,
  lineMode: 'product' | 'offer' | 'reward'
): InvalidLineDiagnostic {
  const lineRootName = entry.name;
  const lineRootId = getLineRootId(entry);

  if (lineMode === 'offer' || lineMode === 'reward') {
    const benefit = entry.children[0];
    if (!benefit) {
      return {
        kind: 'missing_benefit',
        lineMode,
        lineRootName,
        lineRootId,
      };
    }
    const first = findFirstNonAvailableNode(benefit, getOneMenuItemAvailability, []);
    if (!first) {
      return {
        kind: 'unexpected_available',
        lineMode,
        lineRootName,
        lineRootId,
      };
    }
    return {
      kind: 'unavailable',
      lineMode,
      lineRootName,
      lineRootId,
      failingName: first.failing.name,
      failingId: first.failing.id,
      status: first.status,
      ancestorPath: first.ancestorPath,
    };
  }

  const first = findFirstNonAvailableNode(entry, getOneMenuItemAvailability, []);
  if (!first) {
    return {
      kind: 'unexpected_available',
      lineMode: 'product',
      lineRootName,
      lineRootId,
    };
  }
  return {
    kind: 'unavailable',
    lineMode: 'product',
    lineRootName,
    lineRootId,
    failingName: first.failing.name,
    failingId: first.failing.id,
    status: first.status,
    ancestorPath: first.ancestorPath,
  };
}

export function formatInvalidLineDiagnostic(d: InvalidLineDiagnostic): string {
  switch (d.kind) {
    case 'missing_benefit':
      return `No benefit child on line "${d.lineRootName}" (${d.lineRootId})`;
    case 'unexpected_available':
      return `Line "${d.lineRootName}" (${d.lineRootId}): tree reports all AVAILABLE (mismatch with cart validation — re-open cart or retry)`;
    case 'unavailable': {
      const path = d.ancestorPath.length > 0 ? `${d.ancestorPath.join(' → ')} → ` : '';
      return `${path}${d.failingName} (${d.failingId}): ${d.status}`;
    }
  }
}

function dayPartLabel(key: string, allDayParts: ReadonlyArray<IValidDayPart>): string {
  const dp = allDayParts.find(p => p.key === key);
  const locale = dp?.displayName?.locale;
  if (typeof locale === 'string' && locale.trim()) {
    return locale.trim();
  }
  return key;
}

/**
 * Lines for internal diagnostics: which dayparts are active now vs full store menu schedule.
 * Helps interpret OUT_OF_DAYPART on cart lines.
 */
export function formatDaypartsContextLines(
  activeDayParts: IDayPartBoundary[],
  allDayParts: ReadonlyArray<IValidDayPart>
): string[] {
  const lines: string[] = [];

  if (activeDayParts.length === 0) {
    lines.push(
      'Active now: (none — daypart boundaries empty or not computed yet; compare store menu below)'
    );
  } else {
    lines.push('Active now:');
    activeDayParts.forEach(boundary => {
      const label = dayPartLabel(boundary.key, allDayParts);
      const start = boundary.startTime.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      });
      const end = boundary.endTime.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      });
      lines.push(`  · ${label} [${boundary.key}] — ${start}–${end} (device local)`);
    });
  }

  if (allDayParts.length > 0) {
    lines.push('Store menu dayparts (reference):');
    allDayParts.forEach(dp => {
      const label = dayPartLabel(dp.key, allDayParts);
      lines.push(`  · ${label} [${dp.key}] — ${dp.startTime}–${dp.endTime}`);
    });
  }

  return lines;
}
